import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone } from "../controller/global";
import { submitLead } from "../controller/leads";
import { Link } from "react-router";
import { Images } from "../controller/Images";

const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

export const recommendationEngineSEO = {
  title: "AI Recommendation Engine Development for Retail | InQmise",
  description:
    "AI recommendation engines for fashion, apparel and retail — collaborative filtering, content-based and hybrid models, integrated into your storefront.",
  keywords:
    "Recommendation Engine Development, AI Recommendation System, Collaborative Filtering, Personalization Engine, Machine Learning Recommendations, Recommendation Algorithm, E-Commerce Recommendation Engine",
  canonical: "https://inqmisetechnologies.com/recommendation-engine-development",
  og: {
    title: "Recommendation Engine Development | AI-Powered Personalization",
    description: "Boost engagement & conversion with intelligent recommendation engines. Personalized product discovery using advanced AI algorithms.",
    url: "https://inqmisetechnologies.com/recommendation-engine-development",
    type: "website",
    image: "https://inqmisetechnologies.com/logo.png",
  },
  twitter: {
    card: "summary_large_image",
    title: "Recommendation Engine Development | AI-Powered Personalization",
    description: "Personalized product discovery that drives engagement, conversion, and average order value.",
    image: "https://inqmisetechnologies.com/logo.png",
  },
};

const KPI_DASHBOARD = [
  { metric: "Click-Through Rate", after: 75, beforeLabel: "2-5%", afterLabel: "3.5-8%" },
  { metric: "Conversion Rate", after: 65, beforeLabel: "1-3%", afterLabel: "2-5%" },
  { metric: "Average Order Value", after: 80, beforeLabel: "$50", afterLabel: "$75-85" },
  { metric: "Customer Retention", after: 85, beforeLabel: "60%", afterLabel: "75-85%" },
];

const CAPABILITIES = [
  { title: "Collaborative Filtering", desc: "Learn from user behavior patterns to recommend items similar users enjoyed." },
  { title: "Content-Based Recommendations", desc: "Match users with products based on item attributes and user preferences." },
  { title: "Hybrid Recommendation Systems", desc: "Combine multiple algorithms for superior accuracy and diverse recommendations." },
  { title: "Real-Time Personalization", desc: "Dynamic recommendations that evolve instantly based on current user actions." },
  { title: "A/B Testing & Optimization", desc: "Continuously improve recommendation accuracy and conversion rates." },
  { title: "Cold Start Problem Solutions", desc: "Intelligent recommendations for new users and new products." },
];

const ALGORITHMS = ["Collaborative Filtering", "Content-Based Filtering", "Hybrid Models", "Deep Learning Networks", "Contextual Bandits", "Real-time Personalization"];
const INFRASTRUCTURE = ["Real-time Data Pipeline", "Feature Engineering", "A/B Testing Framework", "Analytics & Reporting", "Scalable Database", "CDN Integration"];

const PROCESS_STEPS = [
  { step: "01", title: "Data Audit & Strategy", desc: "Assess your existing data quality and define the personalization strategy that fits your catalog." },
  { step: "02", title: "Algorithm Selection", desc: "Choose collaborative, content-based, or hybrid models suited to your data density." },
  { step: "03", title: "Model Development", desc: "Build and train the recommendation model against your real product and behavior data." },
  { step: "04", title: "A/B Testing", desc: "Validate lift in CTR, conversion, and AOV against your existing experience before full rollout." },
  { step: "05", title: "Deployment", desc: "Ship to production with real-time serving infrastructure and monitoring." },
  { step: "06", title: "Continuous Optimization", desc: "Retrain and tune models as user behavior and catalog evolve." },
];

const INDUSTRIES = [
  { industry: "E-Commerce", benefit: "40-60% higher CTR, 25-40% conversion increase" },
  { industry: "Streaming & Media", benefit: "Reduce churn by 20-30%, increase watch time" },
  { industry: "SaaS", benefit: "Boost feature adoption, improve retention" },
  { industry: "News & Content", benefit: "Increase engagement and time on site" },
  { industry: "Fashion & Retail", benefit: "Personalized style recommendations" },
  { industry: "Music & Audio", benefit: "Discovery-driven engagement" },
];

const CASE_STUDIES = [
  { org: "Online Fashion Retailer", before: "1.8% conversion", after: "3.9% conversion", detail: "Hybrid recommendation model personalized to browsing and purchase history." },
  { org: "Streaming Platform", before: "22% monthly churn", after: "15% monthly churn", detail: "Content-based recommendations tuned to watch history and session context." },
  { org: "B2B Marketplace", before: "$62 AOV", after: "$94 AOV", detail: "Cross-sell recommendations surfaced at checkout using collaborative filtering." },
];

// What moves the price (SEO plan: competitors all answer cost; we explain the
// drivers and quote after scoping rather than publish invented numbers).
const COST_FACTORS = [
  { t: "Catalog size and data", d: "The number of products and how much browsing and purchase history you already collect decide which methods will work and how much preparation is needed." },
  { t: "Where recommendations appear", d: "One placement, such as product pages, is the simplest start. Home page, cart, email and app placements each add work." },
  { t: "Your store platform", d: "Shopify and WooCommerce integrate quickly through their APIs. Custom platforms may need more integration work." },
  { t: "Real-time or batch", d: "Recommendations refreshed nightly are simpler and cheaper to run than ones that react instantly to what a shopper is doing." },
];

const FAQS = [
  {
    q: "How do recommendation engines improve conversion rates?",
    a: "Recommendation engines increase conversion by 25-40% by personalizing product discovery, showing users relevant items they're likely to purchase, and reducing decision fatigue.",
  },
  {
    q: "What algorithms power recommendation systems?",
    a: "Collaborative filtering, content-based filtering, hybrid models, and deep learning networks are used to deliver accurate, personalized recommendations.",
  },
  {
    q: "What is the cold start problem and how do you solve it?",
    a: "The cold start problem occurs when there's not enough data on a new user or product to personalize recommendations. We solve it with content-based fallbacks and popularity-weighted defaults until behavioral data accumulates.",
  },
  {
    q: "How much data do we need before a recommendation engine works well?",
    a: "Collaborative filtering typically needs a few weeks of interaction data at moderate traffic; content-based and hybrid models can perform well from day one using product attributes alone.",
  },
  {
    q: "How long does recommendation engine development take?",
    a: "A production-ready recommendation engine typically takes 6-12 weeks, including data audit, model development, A/B testing, and deployment.",
  },
  {
    q: "How much does recommendation engine development cost?",
    a: "It depends on your catalog size, the data you already collect and where recommendations appear. Starting with one placement, such as product pages, keeps the first phase small. We quote after reviewing your data.",
  },
  {
    q: "Can you add recommendations to our Shopify or WooCommerce store?",
    a: "Yes. We integrate with Shopify, WooCommerce, Magento and custom stores, using their APIs or a lightweight script, without rebuilding your store.",
  },
  {
    q: "Is customer data handled privately?",
    a: "Yes. Recommendations are built from behavior data you already hold, stored in your own environment and processed in line with your privacy policy and applicable laws such as GDPR.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

const RecommendationEngineDevelopment = () => {
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = recommendationEngineSEO.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = recommendationEngineSEO.description;
    document.head.appendChild(description);

    const keywords = document.createElement("meta");
    keywords.name = "keywords";
    keywords.content = recommendationEngineSEO.keywords;
    document.head.appendChild(keywords);

    const ogTitle = document.createElement("meta");
    ogTitle.setAttribute("property", "og:title");
    ogTitle.content = recommendationEngineSEO.og.title;
    document.head.appendChild(ogTitle);

    const ogDesc = document.createElement("meta");
    ogDesc.setAttribute("property", "og:description");
    ogDesc.content = recommendationEngineSEO.og.description;
    document.head.appendChild(ogDesc);

    const ogUrl = document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = recommendationEngineSEO.og.url;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement("meta");
    ogType.setAttribute("property", "og:type");
    ogType.content = recommendationEngineSEO.og.type;
    document.head.appendChild(ogType);

    const ogImage = document.createElement("meta");
    ogImage.setAttribute("property", "og:image");
    ogImage.content = recommendationEngineSEO.og.image;
    document.head.appendChild(ogImage);

    const twitterCard = document.createElement("meta");
    twitterCard.name = "twitter:card";
    twitterCard.content = recommendationEngineSEO.twitter.card;
    document.head.appendChild(twitterCard);

    const twitterTitle = document.createElement("meta");
    twitterTitle.name = "twitter:title";
    twitterTitle.content = recommendationEngineSEO.twitter.title;
    document.head.appendChild(twitterTitle);

    const twitterDesc = document.createElement("meta");
    twitterDesc.name = "twitter:description";
    twitterDesc.content = recommendationEngineSEO.twitter.description;
    document.head.appendChild(twitterDesc);

    const twitterImage = document.createElement("meta");
    twitterImage.name = "twitter:image";
    twitterImage.content = recommendationEngineSEO.twitter.image;
    document.head.appendChild(twitterImage);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", recommendationEngineSEO.canonical);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Recommendation Engine Development",
      "description": recommendationEngineSEO.description,
      "url": recommendationEngineSEO.canonical,
      "provider": {
        "@id": "https://inqmisetechnologies.com/#organization",
        "@type": "Organization",
        "name": "InQmise Technologies",
        "url": "https://inqmisetechnologies.com",
        "telephone": "+918130409466",
        "email": "contact@inqmisetechnologies.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Cabin No. 10, Second Floor, A 24/9, Mohan Cooperative",
          "addressLocality": "Badarpur",
          "addressRegion": "New Delhi",
          "postalCode": "110044",
          "addressCountry": "IN",
        },
      },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "availableLanguage": ["en-IN"],
      "serviceType": "Recommendation Engine Development",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": "Recommendation Engine Development", "item": recommendationEngineSEO.canonical },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Please tell us about your catalog and platform.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — text left, a live KPI-bars panel on the right instead of a
          chat/timeline/flow-diagram visual */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">AI-powered personalization · built to convert</p>
          <h1>
            Recommendation engine development that <em>earns the click</em>.
          </h1>
          <p className="ea-lede">
            We build AI recommendation engines for fashion, apparel and wider retail &mdash; personalized product discovery that increases engagement and drives measurable revenue growth, integrated into the storefront you already run.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to build your recommendation engine">
              Build your engine ↗
            </button>
            <a className="ea-text-link" href="#capabilities">See what it does</a>
          </div>
        </Reveal>

        <Reveal as="div" i={1} reduceMotion={reduceMotion} className="re-hero-kpi">
          {KPI_DASHBOARD.map((kpi) => (
            <div className="re-hero-kpi-row" key={kpi.metric}>
              <div className="re-hero-kpi-row-top">
                <b>{kpi.metric}</b>
                <span>{kpi.beforeLabel} → {kpi.afterLabel}</span>
              </div>
              <div className="re-hero-kpi-bar"><span style={{ width: `${kpi.after}%` }} /></div>
            </div>
          ))}
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* CAPABILITIES — flag-top cards */}
      <section className="ea-wrap ea-section" id="capabilities">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What we build</div>
            <h2>Recommendation<br />capabilities.</h2>
          </div>
          <p>Comprehensive recommendation engine development for e-commerce, fashion, media, and SaaS platforms.</p>
        </Reveal>
        <div className="re-flag-grid">
          {CAPABILITIES.map((item, i) => (
            <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} className="re-flag-card" key={item.title}>
              <div className="re-flag-bar" />
              <div className="re-flag-card-body">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TECH — bare checklists */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Under the hood</div>
              <h2>Advanced algorithms,<br />real infrastructure.</h2>
            </div>
            <p>Chosen for your data density and traffic, not for the pitch deck.</p>
          </Reveal>
          <div className="ea-model-layout" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <Reveal as="div" reduceMotion={reduceMotion}>
              <p style={{ fontFamily: "Syne, sans-serif", fontSize: "var(--t-sm)", letterSpacing: "-.03em", margin: "0 0 16px", color: "var(--ink)" }}>Algorithms</p>
              <ul className="re-bare-list">
                {ALGORITHMS.map((algo) => <li key={algo}>{algo}</li>)}
              </ul>
            </Reveal>
            <Reveal as="div" i={1} reduceMotion={reduceMotion}>
              <p style={{ fontFamily: "Syne, sans-serif", fontSize: "var(--t-sm)", letterSpacing: "-.03em", margin: "0 0 16px", color: "var(--ink)" }}>Infrastructure</p>
              <ul className="re-bare-list">
                {INFRASTRUCTURE.map((tech) => <li key={tech}>{tech}</li>)}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS — plain numbered list */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">A disciplined route to value</div>
            <h2>From data audit to<br />continuous optimization.</h2>
          </div>
          <p>Six stages, from your first data audit to a continuously improving production system.</p>
        </Reveal>
        <div className="re-numbered-list">
          {PROCESS_STEPS.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-numbered-row" key={item.step}>
              <span className="re-numbered-row-num">{item.step}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FASHION & APPAREL — the vertical the queries actually use, and the one
          we have a real engagement behind. Kept short and pointed at the case
          study rather than repeating it. */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Vertical focus</div>
              <h2>Fashion and apparel recommendation engines.</h2>
            </div>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion}>
            <div className="std-overview">
              <p>
                AI fashion recommendation engines have to solve a problem that generic retail
                engines do not. Fashion is the hardest category to recommend in, and the reasons are
                structural rather than technical. A meaningful share of an apparel catalog did not
                exist last season and will be gone by the next, so items rarely accumulate enough
                interaction history to be confidently placed. Taste is not quality &mdash; a garment
                one shopper loves is one another would never wear &mdash; which makes popularity a
                far weaker signal here than in books or film. And intent shifts within a single
                session: the same person shops for work clothes on Monday and a wedding outfit on
                Saturday.
              </p>
              <p>
                That combination is why generic recommendation platforms underperform on apparel and
                why the engines we build for fashion retailers lean on catalog attributes &mdash;
                cut, color, fabric, price band &mdash; alongside behavioral signals, rather than
                behavior alone. Attributes describe a garment from the moment it is created, so a
                new arrival can be positioned without waiting for anyone to interact with it.
              </p>
              <p>
                We built exactly this for a fashion retailer, integrated into their existing
                platform rather than replacing any part of it.{" "}
                <Link className="ea-text-link" to="/case-studies/ai-style-recommendation">
                  Read the case study &#8599;
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES — data table */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where it works</div>
            <h2>Perfect for every<br />industry's catalog.</h2>
          </div>
          <p>Tuned to how each industry's catalog and users actually behave.</p>
        </Reveal>
        <Reveal as="div" reduceMotion={reduceMotion}>
          <table className="re-industry-table">
            <tbody>
              {INDUSTRIES.map((item) => (
                <tr key={item.industry}>
                  <td>{item.industry}</td>
                  <td>{item.benefit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </section>

      {/* CASE STUDIES — results ledger */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Client success stories</div>
            <h2>Representative<br />results, not outliers.</h2>
          </div>
          <p>A sample of recent recommendation engine development engagements.</p>
        </Reveal>
        <div className="re-ledger">
          {CASE_STUDIES.map((item, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-ledger-row" key={item.org}>
              <span className="re-ledger-org">{item.org}</span>
              <span className="re-ledger-stat">
                <span className="re-before">{item.before}</span>
                <span>→</span>
                <span className="re-after">{item.after}</span>
              </span>
              <span className="re-ledger-detail">{item.detail}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COST — what drives the price */}
      <section className="ea-wrap ea-section" id="cost">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Cost</div>
            <h2>What drives the cost of<br />recommendation engine development.</h2>
          </div>
          <p>A recommendation engine can start small and grow. These factors set the cost of the first phase, which we quote as a fixed price after reviewing your data.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {COST_FACTORS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.t} style={{ flex: "1 1 260px" }}>
              <h3>{item.t}</h3>
              <p>{item.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Recommendation engine development FAQs</h2>
            <p>Short answers to the questions we are usually asked before an engagement begins.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? -1 : i)}>
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">Ready when you are</div>
            <h2 id="contact-heading">Ready to boost engagement?</h2>
            <p>Tell us about your catalog and traffic, and our team will get back to you within one business day.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Personalize with confidence.</h3>
            <p>Tell us about your catalog and we'll help you scope it.</p>

            <form onSubmit={handleContactSubmit} aria-label="Recommendation engine development project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="rec-eng-fullname">Full Name</label>
                <input
                  id="rec-eng-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "rec-eng-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="rec-eng-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="rec-eng-phone">Phone Number (optional)</label>
                  <input
                    id="rec-eng-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "rec-eng-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="rec-eng-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="rec-eng-email">Email</label>
                  <input
                    id="rec-eng-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "rec-eng-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="rec-eng-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="rec-eng-subject">Subject</label>
                <input
                  id="rec-eng-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., Product recommendations for our storefront"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "rec-eng-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="rec-eng-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="rec-eng-description">Project Details</label>
                <textarea
                  id="rec-eng-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "rec-eng-description-error" : undefined}
                />
                {contactErrors.description && <p id="rec-eng-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start your recommendation engine ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default RecommendationEngineDevelopment;
